import { useEffect, useState } from 'react';
import './ProgramPages.css';
import {
  IconAtom,
  IconBriefcase,
  IconCap,
  IconChart,
  IconCheck,
  IconGlobe,
  IconLeaf,
  IconStar,
  IconTarget,
} from './ProgramIcons';
import heroImage1 from './assets/news/new2.jpg';
import heroImage2 from './assets/news/news3.jpg';
import heroImage3 from './assets/news/news4.jpg';

function SecondaryPage({ onNavigate }) {
  const [activeHero, setActiveHero] = useState(0);
  const heroSlides = [heroImage1, heroImage2, heroImage3];

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  const programHighlights = [
    {
      icon: IconCap,
      title: 'University Preparation',
      description: 'Rigorous academics aligned with Cambridge IGCSE and A-Level standards for global university readiness.',
    },
    {
      icon: IconStar,
      title: 'Leadership Development',
      description: 'Student council, mentoring programs, and service learning that build confident leaders.',
    },
    {
      icon: IconAtom,
      title: 'Advanced STEM',
      description: 'Laboratory work, research projects, and competitions in science, technology, and mathematics.',
    },
    {
      icon: IconGlobe,
      title: 'Global Perspective',
      description: 'International curriculum, cultural exchange, and preparation for study abroad opportunities.',
    },
  ];

  const pathways = [
    {
      level: 'Junior High (Grades 7-9)',
      title: 'Foundation & Exploration',
      description: 'Building strong academic foundations while exploring interests and developing study skills',
      features: [
        'Core subjects with increased depth',
        'Introduction to sciences and humanities',
        'Study skills and time management',
        'Career exploration and guidance',
        'Leadership opportunities',
      ],
      image: heroImage1,
    },
    {
      level: 'Senior High (Grades 10-12)',
      title: 'Specialization & Mastery',
      description: 'Advanced coursework, university preparation, and pathway specialization',
      features: [
        'Cambridge IGCSE (Grades 10-11)',
        'Cambridge A-Level (Grade 12)',
        'Subject specialization tracks',
        'University counseling and applications',
        'Internships and real-world experience',
      ],
      image: heroImage2,
    },
  ];

  const subjectTracks = [
    {
      track: 'Science Track',
      subjects: ['Advanced Mathematics', 'Physics', 'Chemistry', 'Biology', 'Computer Science'],
      careers: 'Medicine, Engineering, Research, Technology',
    },
    {
      track: 'Social Sciences Track',
      subjects: ['Economics', 'Business Studies', 'Geography', 'History', 'Psychology'],
      careers: 'Business, Law, Social Work, Education',
    },
    {
      track: 'Arts & Humanities Track',
      subjects: ['Literature', 'Languages', 'Art & Design', 'Music', 'Drama'],
      careers: 'Creative Industries, Communications, Arts',
    },
  ];

  const studentSupport = [
    {
      title: 'Academic Counseling',
      description: 'One-on-one guidance for course selection, study strategies, and academic goals.',
      icon: IconChart,
    },
    {
      title: 'University Guidance',
      description: 'Expert support for university applications, essays, and scholarship opportunities.',
      icon: IconTarget,
    },
    {
      title: 'Career Counseling',
      description: 'Career assessments, internships, and connections with industry professionals.',
      icon: IconBriefcase,
    },
    {
      title: 'Personal Development',
      description: 'Mentoring, wellbeing support, and character formation programs.',
      icon: IconLeaf,
    },
  ];

  const achievements = [
    { number: '95%', label: 'University Acceptance Rate' },
    { number: '85%', label: 'Cambridge A*/A Grades' },
    { number: '20+', label: 'University Destinations' },
    { number: '100%', label: 'Graduate Employment/Study' },
  ];

  const opportunities = [
    'Model United Nations',
    'Science Olympiads',
    'Debate & Public Speaking',
    'Sports Competitions',
    'Arts & Music Performances',
    'Community Service Projects',
    'Student Exchange Programs',
    'Entrepreneurship Club',
  ];

  return (
    <div className="program-page secondary-page" id="secondary">
      {/* Hero Section */}
      <section className="program-hero">
        <div className="program-hero-media">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`program-hero-slide ${index === activeHero ? 'active' : ''}`}
            >
              <img src={slide} alt="Secondary Program" />
            </div>
          ))}
        </div>
        <div className="program-hero-overlay" />
        <div className="program-hero-content">
          <span className="program-hero-badge">Grades 7-12</span>
          <h1>Secondary Program</h1>
          <p className="program-hero-subtitle">
            Preparing confident, capable leaders for university and beyond
          </p>
          <div className="program-hero-stats">
            <div className="program-stat">
              <span className="stat-number">28</span>
              <span className="stat-label">Max Class Size</span>
            </div>
            <div className="program-stat">
              <span className="stat-number">3</span>
              <span className="stat-label">Specialization Tracks</span>
            </div>
            <div className="program-stat">
              <span className="stat-number">95%</span>
              <span className="stat-label">University Acceptance</span>
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="program-overview">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Program Overview</span>
            <h2>Excellence in Secondary Education</h2>
            <p>
              Our secondary program combines rigorous Cambridge-aligned academics with character 
              development and leadership training. We prepare students not just for university, 
              but for meaningful lives of purpose and impact.
            </p>
          </div>

          <div className="program-highlights-grid">
            {programHighlights.map((item, index) => {
              const Icon = item.icon;
              return (
              <div className="highlight-card" key={index}>
                <div className="highlight-icon" aria-hidden="true">
                  <Icon />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pathways */}
      <section className="pathways-section">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Academic Pathways</span>
            <h2>Junior High to Senior High</h2>
            <p>A progressive journey from foundation to university readiness</p>
          </div>

          <div className="pathways-grid">
            {pathways.map((pathway, index) => (
              <div className="pathway-card" key={index}>
                <div className="pathway-image">
                  <img src={pathway.image} alt={pathway.level} />
                </div>
                <div className="pathway-content">
                  <span className="pathway-level">{pathway.level}</span>
                  <h3>{pathway.title}</h3>
                  <p>{pathway.description}</p>
                  <ul className="pathway-features">
                    {pathway.features.map((feature, idx) => (
                      <li key={idx}>
                        <span className="check-icon" aria-hidden="true">
                          <IconCheck />
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subject Tracks */}
      <section className="tracks-section">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Specialization</span>
            <h2>Subject Tracks</h2>
            <p>Choose your pathway based on interests and university goals</p>
          </div>

          <div className="tracks-grid">
            {subjectTracks.map((track, index) => (
              <div className="track-card" key={index}>
                <h3>{track.track}</h3>
                <div className="track-subjects">
                  {track.subjects.map((subject, idx) => (
                    <span className="subject-tag" key={idx}>{subject}</span>
                  ))}
                </div>
                <div className="track-careers">
                  <strong>Career Paths:</strong>
                  <p>{track.careers}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Student Support */}
      <section className="support-section">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Student Support</span>
            <h2>Guidance & Counseling</h2>
            <p>Comprehensive support for academic, personal, and career development</p>
          </div>

          <div className="support-grid">
            {studentSupport.map((support, index) => {
              const Icon = support.icon;
              return (
                <div className="support-card" key={index}>
                  <div className="support-icon" aria-hidden="true">
                    <Icon />
                  </div>
                  <h4>{support.title}</h4>
                  <p>{support.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="achievements-section">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Our Track Record</span>
            <h2>Student Success</h2>
            <p>Results that speak for themselves</p>
          </div>

          <div className="achievements-grid">
            {achievements.map((achievement, index) => (
              <div className="achievement-card" key={index}>
                <div className="achievement-number">{achievement.number}</div>
                <div className="achievement-label">{achievement.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunities */}
      <section className="opportunities-section">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Beyond Academics</span>
            <h2>Leadership & Enrichment</h2>
            <p>Opportunities to grow, compete, and make a difference</p>
          </div>

          <div className="opportunities-grid">
            {opportunities.map((opportunity, index) => (
              <div className="opportunity-card" key={index}>
                <span className="opportunity-icon" aria-hidden="true">
                  <IconStar />
                </span>
                <span className="opportunity-name">{opportunity}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="parent-info">
        <div className="program-container">
          <div className="cta-section">
            <h3>Ready to Begin Your Secondary Journey?</h3>
            <p>Join a community of ambitious learners preparing for global success</p>
            <div className="cta-buttons">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onNavigate && onNavigate('register')}
              >
                Apply Now
              </button>
              <a href="/contact" className="btn btn-secondary">Book a Campus Tour</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SecondaryPage;
